import type { AgentDto } from '@af/shared';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentSectionLinks } from '@/components/agent/AgentSectionLinks';
import { effectiveDecision } from '@/components/agent/ToolsFields';
import { AgentOrb } from '@/components/ui/agent-orb';
import { buttonVariants, IconButton, Spinner } from '@/components/ui/button';
import { Menu, MenuContent, MenuItem, MenuLabel, MenuTrigger } from '@/components/ui/dropdown';
import { IconChevron, IconLock, IconPanelRight } from '@/components/ui/icons';
import { Badge, Empty, ErrorBox, Stat, StatGroup } from '@/components/ui/misc';
import { StatusDot } from '@/components/ui/status-dot';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BUILTIN_TOOLS, EFFECT_TONE } from '@/lib/catalog';
import { isoDate, ms, relTime, tokens, usd } from '@/lib/format';
import { useAgentsLive } from '@/lib/live-agents';
import type { OnboardingStepId } from '@/lib/onboarding';
import { type ContextKind, contextKindFor } from '@/lib/page-context';
import {
  useAgents,
  useApprovals,
  useBudgets,
  useMe,
  useNotifications,
  useOrgStatus,
  useRuns,
  useUsage,
} from '@/lib/queries';
import { cn } from '@/lib/utils';
import { LiveStatusLabel } from './AgentsLive';
import {
  activityText,
  IdleAgents,
  NeedsYou,
  RecentlyFinished,
  StatusHero,
  TodayTiles,
  WorkingNow,
} from './LiveOverview';
import { MiniBars } from './MiniBars';
import { PageContext } from './PageContext';

const DAY = 86_400_000;

function DecisionPill({ decision, enabled = true }: { decision: string; enabled?: boolean }) {
  if (!enabled) return <span className="text-xs text-faint">off</span>;
  if (decision === 'allow')
    return <span className="inline-flex items-center gap-1 text-xs text-success">✓ Allowed</span>;
  if (decision === 'ask')
    return <span className="inline-flex items-center gap-1 text-xs text-warn">? Ask</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted">
      <IconLock size={12} /> Blocked
    </span>
  );
}

function EmptyHint({ text, cta, step }: { text: string; cta: string; step: OnboardingStepId }) {
  return (
    <div className="rounded-lg border border-dashed border-border-strong px-3 py-4 text-center">
      <p className="text-sm text-muted">{text}</p>
      <Link
        to="/onboarding"
        search={{ step }}
        className="mt-1.5 inline-block text-xs text-accent hover:underline"
      >
        {cta}
      </Link>
    </div>
  );
}

function Dashboard({ agent, context }: { agent: AgentDto | null; context: ContextKind }) {
  const { t } = useTranslation();
  const _me = useMe();
  const status = useOrgStatus();
  const live = useAgentsLive({ tails: true });
  const agents = useAgents();
  const budgets = useBudgets();
  const pending = useApprovals('pending');
  const notifications = useNotifications();
  const runs = useRuns(agent ? { agentId: agent.id } : undefined);
  const [range] = useState(() => ({
    from: isoDate(new Date(Date.now() - 13 * DAY)),
    to: isoDate(new Date()),
  }));
  const daily = useUsage({ ...range, groupBy: 'day', agentId: agent?.id });
  const byModel = useUsage({ ...range, groupBy: 'model', agentId: agent?.id });
  const today = useUsage({ from: range.to, to: range.to, groupBy: 'day', agentId: agent?.id });

  const bars = useMemo(() => {
    const map = new Map((daily.data?.rows ?? []).map((r) => [r.key, r.costUsd]));
    return Array.from({ length: 14 }, (_, i) => {
      const key = isoDate(new Date(Date.now() - (13 - i) * DAY));
      return { key, value: map.get(key) ?? 0 };
    });
  }, [daily.data]);
  const total = bars.reduce((s, b) => s + b.value, 0);

  const budget = (budgets.data ?? []).find((b) =>
    agent
      ? b.scope === 'agent' && b.scopeId === agent.id && b.period === 'day'
      : b.scope === 'org' && b.period === 'day',
  );
  const budgetPct = budget?.limitUsd ? Math.round((budget.spentUsd / budget.limitUsd) * 100) : null;
  const pendingForScope = (pending.data ?? []).filter((a) => !agent || a.agentId === agent.id);
  const halted = status.data?.agentsHalted;

  const liveOf = agent ? live.list.find((l) => l.agent.id === agent.id) : undefined;

  return (
    <div className="flex flex-col gap-4 px-4 pt-3 pb-5">
      {agent ? <AgentSectionLinks agentId={agent.id} /> : null}
      <Tabs defaultValue="overview">
        <TabsList aria-label="Dashboard sections">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="activity">
            Activity
            {pendingForScope.length ? (
              <span className="ml-1.5 rounded-xs bg-warn/15 px-1 text-2xs text-warn">
                {pendingForScope.length}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="flex flex-col gap-5 pt-4">
          {!agent ? <PageContext kind={context} /> : null}
          {agent ? (
            <>
              <div className="flex items-start gap-3">
                <AgentOrb seed={agent.id} size={40} avatar={agent.avatar} name={agent.name} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-xl font-semibold tracking-tight">{agent.name}</h3>
                    {liveOf ? <LiveStatusLabel status={liveOf.status} /> : null}
                  </div>
                  <p className="mt-0.5 text-sm text-muted">
                    {liveOf && (liveOf.status === 'running' || liveOf.status === 'approval')
                      ? activityText(t, liveOf)
                      : agent.status === 'paused'
                        ? t('inspector.agentPaused')
                        : liveOf?.run
                          ? t('inspector.lastActive', {
                              when: relTime(liveOf.run.endedAt ?? liveOf.run.createdAt),
                            })
                          : agent.roleTitle || t('inspector.noRuns')}
                  </p>
                </div>
              </div>
              {liveOf ? <NeedsYou live={[liveOf]} /> : null}
              {liveOf?.status === 'running' ? <WorkingNow live={[liveOf]} /> : null}
              <TodayTiles
                runs={runs.data ?? []}
                spentUsd={today.data?.totals.costUsd ?? 0}
                budgetPct={budgetPct}
              />
              <RecentlyFinished runs={runs.data ?? []} agents={agents.data} />
              <details className="group rounded-xl border border-border">
                <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2.5 text-sm text-fg-2">
                  {t('inspector.toolAccess')}
                  <IconChevron size={12} className="text-faint transition-transform group-open:rotate-90" />
                </summary>
                <ul className="border-t border-border">
                  {BUILTIN_TOOLS.filter((tool) => !tool.phase).map((tool) => {
                    const on = agent.currentVersion?.builtinTools.includes(tool.name) ?? false;
                    const decision = effectiveDecision(tool, agent.currentVersion?.toolPolicy ?? []);
                    return (
                      <li
                        key={tool.name}
                        className={cn(
                          'flex h-10 items-center gap-2 border-b border-border px-3 last:border-b-0',
                          !on && 'opacity-60',
                        )}
                      >
                        <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">{tool.name}</span>
                        <Badge
                          tone={EFFECT_TONE[tool.effect] === 'neutral' ? 'neutral' : EFFECT_TONE[tool.effect]}
                        >
                          {tool.effect}
                        </Badge>
                        <span className="w-20 text-right">
                          <DecisionPill decision={decision} enabled={on} />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </details>
            </>
          ) : live.list.length === 0 ? (
            <EmptyHint text={t('inspector.noAgents')} cta={t('inspector.createAgent')} step="agent" />
          ) : (
            <>
              <div>
                <StatusHero live={live.list} halted={!!halted} />
                <p className="mt-1 text-sm text-muted">
                  {t('inspector.hero.sub', {
                    count: live.list.length,
                    spent: usd(today.data?.totals.costUsd ?? status.data?.costTodayUsd ?? 0, 2),
                  })}
                </p>
              </div>
              <NeedsYou live={live.list} />
              <WorkingNow live={live.list} />
              <IdleAgents live={live.list} />
              <TodayTiles runs={live.runs} spentUsd={today.data?.totals.costUsd ?? 0} budgetPct={budgetPct} />
              {live.runs.length === 0 && agents.data?.length ? (
                <EmptyHint text={t('inspector.noRuns')} cta={t('inspector.sayHello')} step="hello" />
              ) : (
                <RecentlyFinished runs={live.runs} agents={agents.data} />
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="usage" className="flex flex-col gap-4 pt-4">
          <div>
            <div className="mb-2 flex items-baseline justify-between text-sm">
              <span className="text-fg-2">{t('inspector.cost14d')}</span>
              <span className="font-mono font-semibold">{usd(total, 2)}</span>
            </div>
            <MiniBars data={bars} format={(n) => usd(n, 2)} label="Cost per day, last 14 days" />
          </div>
          <StatGroup>
            <Stat
              label="Tokens in"
              value={tokens(daily.data?.totals.inputTokens ?? 0)}
              sub={`cache ${tokens(daily.data?.totals.cacheReadTokens ?? 0)}`}
            />
            <Stat
              label="Tokens out"
              value={tokens(daily.data?.totals.outputTokens ?? 0)}
              sub={`reasoning ${tokens(daily.data?.totals.reasoningTokens ?? 0)}`}
            />
            <Stat
              label={t('inspector.responseTime')}
              value={ms(daily.data?.latencyP50Ms ?? null)}
              sub={`${((daily.data?.successRate ?? 1) * 100).toFixed(0)}% ${t('inspector.success')}`}
            />
          </StatGroup>
          <div>
            <div className="mb-2 text-sm text-fg-2">By model · 14d</div>
            <ul className="overflow-hidden rounded-lg border border-border">
              {(byModel.data?.rows ?? []).map((r) => (
                <li
                  key={r.key}
                  className="flex h-10 items-center gap-2 border-b border-border px-3 last:border-b-0"
                >
                  <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">{r.label}</span>
                  <span className="font-mono text-xs text-muted">
                    {tokens(r.inputTokens + r.outputTokens)}
                  </span>
                  <span className="w-16 text-right font-mono text-xs">{usd(r.costUsd, 2)}</span>
                </li>
              ))}
            </ul>
          </div>
          <Link to="/usage" className="text-xs text-accent hover:underline">
            Open usage dashboard →
          </Link>
        </TabsContent>

        <TabsContent value="activity" className="flex flex-col gap-4 pt-4">
          <div>
            <div className="mb-2 flex items-baseline justify-between text-sm">
              <span className="text-fg-2">Awaiting approval</span>
              <Link to="/approvals" className="text-xs text-accent hover:underline">
                Inbox
              </Link>
            </div>
            {pendingForScope.length === 0 ? (
              <p className="text-sm text-muted">Nothing waiting.</p>
            ) : (
              <ul className="overflow-hidden rounded-lg border border-warn/30">
                {pendingForScope.map((a) => (
                  <li key={a.id} className="border-b border-border px-3 py-2 last:border-b-0">
                    <div className="font-mono text-[12.5px]">{a.toolName}</div>
                    <div className="truncate text-xs text-muted">{a.inputPreview}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <div className="mb-2 text-sm text-fg-2">Notifications</div>
            <ul className="overflow-hidden rounded-lg border border-border">
              {(notifications.data ?? []).slice(0, 6).map((n) => (
                <li
                  key={n.id}
                  className="flex items-start gap-2 border-b border-border px-3 py-2 last:border-b-0"
                >
                  <StatusDot tone={n.readAt ? 'idle' : 'info'} className="mt-1.5" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{n.title}</span>
                    <span className="block truncate text-xs text-muted">{n.body}</span>
                  </span>
                  <span className="text-xs text-faint">{relTime(n.createdAt).replace(' ago', '')}</span>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Right column: a collapsible context inspector for the org or the focused agent — title row with a
 * scope switcher, Overview / Usage / Activity tabs, KPI tiles, cost bars, agents and latest runs.
 */
export function RightPanel({
  focusedAgentId,
  selectedAgentId,
  onCollapse,
  pathname = '/',
}: {
  focusedAgentId: string | null;
  selectedAgentId?: string | null;
  onCollapse: () => void;
  /** The current page: runs, company, approvals and tasks show their own block above the overview. */
  pathname?: string;
}) {
  const { t } = useTranslation();
  const context = contextKindFor(pathname);
  const agents = useAgents();
  const me = useMe();
  const live = useAgentsLive();
  // 'auto' follows the focused pane's agent; otherwise an explicit scope ('org' or an agent id).
  const [scope, setScope] = useState<string>('auto');
  const byId = new Map((agents.data ?? []).map((a) => [a.id, a]));
  // On a page with its own block the panel stays on the organisation, not on whichever chat was focused last.
  const selected =
    scope === 'auto' ? (selectedAgentId ?? (context ? 'org' : (focusedAgentId ?? 'org'))) : scope;
  const candidate = selected === 'org' ? null : byId.get(selected);
  const agent = candidate?.status !== 'archived' ? (candidate ?? null) : null;
  const org = me.data?.organization;
  const liveOf = agent ? live.list.find((l) => l.agent.id === agent.id) : null;

  return (
    <aside
      aria-label={t('inspector.label')}
      className="flex h-full min-h-0 flex-col overflow-hidden bg-panel"
    >
      <div className="flex min-h-14 shrink-0 flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        {agent ? (
          <AgentOrb seed={agent.id} size={22} avatar={agent.avatar} name={agent.name} />
        ) : (
          <AgentOrb seed={org?.id ?? 'org'} size={22} />
        )}
        <Menu>
          <MenuTrigger
            aria-label={t('inspector.scope')}
            className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-left hover:bg-hover"
          >
            <span className="truncate text-[15px] font-medium text-fg">
              {agent
                ? agent.name
                : selected !== 'org'
                  ? t('inspector.agentUnavailable')
                  : (org?.name ?? t('inspector.organization'))}
            </span>
            {agent && scope === 'auto' ? (
              <span className="text-2xs text-faint">{t('inspector.focused')}</span>
            ) : null}
            <IconChevron size={13} className="shrink-0 rotate-90 text-muted" />
          </MenuTrigger>
          <MenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
            <MenuLabel>{t('inspector.scope')}</MenuLabel>
            <MenuItem onSelect={() => setScope('org')}>
              <AgentOrb seed={org?.id ?? 'org'} size={14} /> {org?.name ?? t('inspector.organization')}
            </MenuItem>
            {(agents.data ?? [])
              .filter((a) => a.status !== 'archived')
              .map((a) => (
                <MenuItem key={a.id} onSelect={() => setScope(a.id)}>
                  <AgentOrb seed={a.id} size={14} avatar={a.avatar} />
                  <span className="flex-1 truncate">{a.name}</span>
                  <span className="truncate text-xs text-muted">{a.roleTitle}</span>
                </MenuItem>
              ))}
            {focusedAgentId || selectedAgentId ? (
              <MenuItem onSelect={() => setScope('auto')}>↺ {t('inspector.focused')}</MenuItem>
            ) : null}
          </MenuContent>
        </Menu>
        {liveOf ? <LiveStatusLabel status={liveOf.status} /> : null}
        {agent ? (
          <Link
            to="/agents/$id"
            params={{ id: agent.id }}
            search={{ tab: 'overview' }}
            className={cn(buttonVariants('ghost'), 'shrink-0')}
          >
            {t('companyPage.openAgent')}
          </Link>
        ) : null}
        <IconButton label={t('inspector.collapse')} onClick={onCollapse} className="ml-auto">
          <IconPanelRight size={15} />
        </IconButton>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {agents.isPending ? (
          <Spinner />
        ) : agents.error ? (
          <ErrorBox error={agents.error} />
        ) : selected !== 'org' && !agent ? (
          <Empty>{t('inspector.agentUnavailable')}</Empty>
        ) : (
          <Dashboard key={agent?.id ?? 'org'} agent={agent} context={context} />
        )}
      </div>
    </aside>
  );
}
